/**
 * Product Add-ons Section - Custom Elements
 * For Shopify Focal Theme
 * 
 * Web Components:
 * - <product-addons> - Main container
 * - <addon-item> - Individual add-on product
 * - <addon-popup> - Modal popup with slider
 * - <addon-slider> - Image slider inside popup
 */

/**
 * Main Product Add-ons Container
 */
class ProductAddons extends HTMLElement {
  constructor() {
    super();
    this.selectedAddons = new Map();
  }

  connectedCallback() {
    this.visibleCount = parseInt(this.dataset.visibleCount) || 2;
    this.toggleBtn = this.querySelector('[data-toggle-addons]');
    this.toggleText = this.querySelector('[data-toggle-text]');
    this.items = this.querySelectorAll('addon-item');
    this.isExpanded = false;

    this.initToggleButton();
    // this.initFormInterception();
  }

  initToggleButton() {
    if (!this.toggleBtn) return;

    this.toggleBtn.addEventListener('click', () => {
      this.isExpanded = !this.isExpanded;
      this.toggleBtn.setAttribute('data-expanded', this.isExpanded);

      this.items.forEach((item, index) => {
        if (index >= this.visibleCount) {
          item.classList.toggle('product-addon-item--hidden', !this.isExpanded);
          item.classList.toggle('product-addon-item--visible', this.isExpanded);
        }
      });

      if (this.toggleText) {
        this.toggleText.textContent = this.isExpanded 
          ? this.toggleText.dataset.openText 
          : this.toggleText.dataset.closeText;
      }
    });
  }

  initFormInterception() {
    document.addEventListener('submit', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(e) {
  const form = e.target;
  if (!form.matches('form[action*="/cart/add"]')) return;
  if (this.selectedAddons.size === 0) return;

  e.preventDefault();

  const formData = new FormData(form);
  const formId = form.id;

  // Capture external fields linked via form attribute
  if (formId) {
    const externalFields = document.querySelectorAll(`[form="${formId}"]`);
    externalFields.forEach(field => {
      if (field.name && !field.disabled) {
        if (field.type === 'file') {
          if (field.files.length > 0) {
            formData.append(field.name, field.files[0]);
          }
        } else if (field.type === 'checkbox' || field.type === 'radio') {
          if (field.checked) {
            formData.append(field.name, field.value);
          }
        } else {
          formData.append(field.name, field.value);
        }
      }
    });
  }

  // Debug
  console.log('All captured fields:');
  for (const [key, value] of formData.entries()) {
    console.log(`${key}:`, value);
  }

  fetch('/cart/add.js', {
    method: 'POST',
    body: formData
  })
  .then(response => {
    if (!response.ok) throw new Error('Failed to add main product');
    return response.json();
  })
  .then(() => {
    const addonItems = [];
    this.selectedAddons.forEach(addon => {
      addonItems.push({ 
        id: addon.variantId, 
        quantity: addon.quantity 
      });
    });

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: addonItems })
    });
  })
  .then(response => {
    if (!response.ok) throw new Error('Failed to add addons');
    return response.json();
  })
  .then(() => {
    this.clearSelection();
    this.handlePostAddToCart(form);
  })
  .catch(error => {
    console.error('Error:', error);
    this.handlePostAddToCart(form);
  });
}

  async addItemsToCart(items, form) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) throw new Error('Failed to add items to cart');

      const result = await response.json();

      this.dispatchEvent(new CustomEvent('cart:items-added', {
        bubbles: true,
        detail: { items: result.items }
      }));

      this.clearSelection();
      this.handlePostAddToCart(form);

    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: error.message }
      }));
      form.submit();
    }
  }

  handlePostAddToCart(form) {
    const cartType = document.body.dataset.cartType || 'drawer';

    if (cartType === 'drawer') {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } else if (cartType === 'page') {
      window.location.href = '/cart';
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  addAddon(id, data) {
    // Last line of defence: never queue a sold-out variant for /cart/add.
    const item = this.querySelector(`addon-item[data-addon-id="${id}"]`);
    if (item && item.dataset.addonAvailable === 'false') {
      this.selectedAddons.delete(id);
      this.dispatchSelectionEvent();
      return;
    }

    this.selectedAddons.set(id, data);
    this.dispatchSelectionEvent();
  }

  removeAddon(id) {
    this.selectedAddons.delete(id);
    this.dispatchSelectionEvent();
  }

  dispatchSelectionEvent() {
    this.dispatchEvent(new CustomEvent('addons:selection-changed', {
      bubbles: true,
      detail: {
        selectedAddons: Array.from(this.selectedAddons.entries()),
        totalPrice: this.getTotalPrice()
      }
    }));
  }

  getTotalPrice() {
    let total = 0;
    this.selectedAddons.forEach(addon => {
      total += addon.price * addon.quantity;
    });
    return total;
  }

  /**
   * Called when /cart/add rejects the add-on batch (e.g. it sold out between
   * page render and submit). Flips the affected rows to the sold-out state so
   * the customer sees why, instead of the item silently vanishing.
   */
  markSoldOut(variantIds) {
    const ids = (variantIds || []).map(String);

    this.querySelectorAll('addon-item').forEach((item) => {
      if (ids.includes(String(item.dataset.addonVariant)) && item.applyAvailability) {
        item.applyAvailability(false);
      }
    });

    this.dispatchSelectionEvent();
  }

  clearSelection() {
    this.selectedAddons.clear();
    this.items.forEach(item => item.deselect());
    this.dispatchSelectionEvent();
  }

  getSelectedAddons() {
    return Array.from(this.selectedAddons.entries());
  }
}


/**
 * Individual Add-on Item
 */
class AddonItem extends HTMLElement {
  constructor() {
    super();
    this.isSelected = false;
  }

  connectedCallback() {
    this.addonId = this.dataset.addonId;
    this.variantId = this.dataset.addonVariant;
    this.price = parseFloat(this.dataset.addonPrice) || 0;
    
    this.toggleBtn = this.querySelector('[data-addon-toggle]');
    this.infoBtn = this.querySelector('[data-addon-popup-trigger]');
    this.popup = this.querySelector('addon-popup');
    this.expandable = this.querySelector('[data-addon-expandable]');
    this.variantSelector = this.querySelector('[data-variant-selector]');
    this.priceEl = this.querySelector('[data-price]');
    this.comparePriceEl = this.querySelector('[data-compare-price]');
    this.discountBadgeEl = this.querySelector('[data-discount-badge]');
    this.variantImageEl = this.querySelector('[data-variant-image]');
    this.soldOutBadgeEl = this.querySelector('[data-sold-out-badge]');

    // Rendered from Shopify `variant.available` — no manual toggling required.
    this.available = this.dataset.addonAvailable !== 'false';

    this.initToggle();
    this.initInfoButton();
    this.initVariantSelector();
    this.applyAvailability(this.available);
  }

  /**
   * Reflect stock status across the row: badge, greyed styling, disabled toggle.
   * Also drops the add-on from the cart payload if it just became unavailable.
   */
  applyAvailability(available) {
    this.available = !!available;
    this.dataset.addonAvailable = this.available;
    this.classList.toggle('product-addon-item--sold-out', !this.available);

    if (this.soldOutBadgeEl) {
      this.soldOutBadgeEl.hidden = this.available;
    }

    if (this.toggleBtn) {
      this.toggleBtn.disabled = !this.available;
      if (this.available) {
        this.toggleBtn.removeAttribute('aria-disabled');
      } else {
        this.toggleBtn.setAttribute('aria-disabled', 'true');
      }
    }

    if (!this.available && this.isSelected) {
      this.deselect();
      const container = this.closest('product-addons');
      if (container) container.removeAddon(this.addonId);
    }
  }

  initToggle() {
    if (!this.toggleBtn) return;

    this.toggleBtn.addEventListener('click', () => {
      // Sold-out add-ons can never be selected — prevents the silent /cart/add 422.
      if (!this.available) return;

      this.isSelected = !this.isSelected;
      this.updateToggleState();
      
      const container = this.closest('product-addons');
      
      if (this.isSelected) {
        this.classList.add('product-addon-item--selected');
        this.showExpandable();
        if (container) {
          container.addAddon(this.addonId, {
            variantId: this.variantId,
            price: this.price,
            quantity: 1
          });
        }
      } else {
        this.classList.remove('product-addon-item--selected');
        this.hideExpandable();
        if (container) {
          container.removeAddon(this.addonId);
        }
      }
    });
  }

  updateToggleState() {
    if (this.toggleBtn) {
      this.toggleBtn.setAttribute('aria-checked', this.isSelected.toString());
    }
  }

  showExpandable() {
    if (this.expandable) {
      this.expandable.setAttribute('aria-hidden', 'false');
    }
  }

  hideExpandable() {
    if (this.expandable) {
      this.expandable.setAttribute('aria-hidden', 'true');
    }
  }

  initInfoButton() {
    if (!this.infoBtn || !this.popup) return;

    this.infoBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.popup.open();
    });
  }

  initVariantSelector() {
    if (!this.variantSelector) return;

    const variantInputs = this.variantSelector.querySelectorAll('input[type="radio"]');
    
    variantInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.handleVariantChange(input);
      });
    });
  }

  handleVariantChange(input) {
    // A disabled radio can still be reached programmatically — re-check stock.
    if (input.dataset.variantAvailable === 'false') {
      this.applyAvailability(false);
      return;
    }
    this.applyAvailability(true);

    const newVariantId = input.dataset.variantId;
    const newPrice = parseFloat(input.dataset.variantPrice) || 0;
    const newComparePrice = parseFloat(input.dataset.variantComparePrice) || 0;
    const newImage = input.dataset.variantImage;

    // Update internal state
    this.variantId = newVariantId;
    this.price = newPrice;
    this.dataset.addonVariant = newVariantId;
    this.dataset.addonPrice = newPrice;

    // Update price display
    if (this.priceEl) {
      this.priceEl.textContent = this.formatMoney(newPrice);
    }

    // Update compare price and discount badge
    if (newComparePrice && newComparePrice > newPrice) {
      const discountPercent = Math.round(((newComparePrice - newPrice) / newComparePrice) * 100);
      
      if (this.comparePriceEl) {
        this.comparePriceEl.textContent = this.formatMoney(newComparePrice);
        this.comparePriceEl.style.display = '';
      }
      
      if (this.discountBadgeEl) {
        this.discountBadgeEl.textContent = `-${discountPercent}%`;
        this.discountBadgeEl.style.display = '';
      }
    } else {
      if (this.comparePriceEl) {
        this.comparePriceEl.style.display = 'none';
      }
      if (this.discountBadgeEl) {
        this.discountBadgeEl.style.display = 'none';
      }
    }

    // Update image if variant has one
    if (newImage && this.variantImageEl) {
      this.variantImageEl.src = newImage;
      this.variantImageEl.srcset = `${newImage} 1x, ${newImage.replace('width=120', 'width=240')} 2x`;
    }

    // Update active state on variant options
    const options = this.variantSelector.querySelectorAll('.variant-option');
    options.forEach(option => {
      const optionInput = option.querySelector('input');
      option.classList.toggle('variant-option--active', optionInput.value === newVariantId);
    });

    // If already selected, update the cart data
    if (this.isSelected) {
      const container = this.closest('product-addons');
      if (container) {
        container.addAddon(this.addonId, {
          variantId: this.variantId,
          price: this.price,
          quantity: 1
        });
      }
    }

    // Dispatch variant change event
    this.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      detail: {
        addonId: this.addonId,
        variantId: newVariantId,
        price: newPrice,
        comparePrice: newComparePrice
      }
    }));
  }

  formatMoney(cents) {
    const amount = (cents / 100);
    const currency = document.querySelector("product-addons.product-addons")?.dataset.currency;

    const symbolAfterCurrencies = ['€', 'kr', 'zł', 'Kč', 'lei', '₴'];
    
    if (symbolAfterCurrencies.includes(currency)) {
      return `${amount} ${currency}`;
    }
    
    return `${currency} ${amount}`;
  }

  select() {
    if (!this.isSelected) {
      this.isSelected = true;
      this.updateToggleState();
      this.classList.add('product-addon-item--selected');
      this.showExpandable();
    }
  }

  deselect() {
    if (this.isSelected) {
      this.isSelected = false;
      this.updateToggleState();
      this.classList.remove('product-addon-item--selected');
      this.hideExpandable();
    }
  }
}


/**
 * Popup Modal Component
 */
class AddonPopup extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    this.overlay = this.querySelector('.product-addon-popup__overlay');
    this.closeBtn = this.querySelector('[data-addon-popup-close]');
    this.slider = this.querySelector('addon-slider');

    this.initCloseHandlers();
    this.initKeyboardHandler();
  }

  initCloseHandlers() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
  }

  initKeyboardHandler() {
    this.keyHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };
  }

  open() {
    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('addon-popup-open');
    document.addEventListener('keydown', this.keyHandler);

    // Reset slider to first slide
    if (this.slider) {
      this.slider.goToSlide(0);
    }

    // Focus management
    const focusable = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length > 0) {
      focusable[0].focus();
    }

    this.dispatchEvent(new CustomEvent('popup:opened', { bubbles: true }));
  }

  close() {
    this.isOpen = false;
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('addon-popup-open');
    document.removeEventListener('keydown', this.keyHandler);

    // Return focus to trigger
    const item = this.closest('addon-item');
    if (item) {
      const trigger = item.querySelector('[data-addon-popup-trigger]');
      if (trigger) trigger.focus();
    }

    this.dispatchEvent(new CustomEvent('popup:closed', { bubbles: true }));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.keyHandler);
  }
}


/**
 * Image Slider Component
 */
class AddonSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.track = this.querySelector('.popup-slider__track');
    this.slides = this.querySelectorAll('.popup-slider__slide');
    this.prevBtn = this.querySelector('[data-slider-prev]');
    this.nextBtn = this.querySelector('[data-slider-next]');
    this.dots = this.querySelectorAll('[data-slider-dot]');

    if (this.slides.length <= 1) return;

    this.initNavigation();
    this.initTouch();
    this.initKeyboard();
  }

  initNavigation() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });
  }

  initTouch() {
    let touchStartX = 0;
    let touchEndX = 0;

    this.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }

  handleSwipe(startX, endX) {
    const threshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  }

  initKeyboard() {
    this.setAttribute('tabindex', '0');
    
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
      } else if (e.key === 'ArrowRight') {
        this.next();
      }
    });
  }

  prev() {
    const newIndex = this.currentIndex === 0 
      ? this.slides.length - 1 
      : this.currentIndex - 1;
    this.goToSlide(newIndex);
  }

  next() {
    const newIndex = this.currentIndex === this.slides.length - 1 
      ? 0 
      : this.currentIndex + 1;
    this.goToSlide(newIndex);
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    this.currentIndex = index;

    // Update track position
    if (this.track) {
      this.track.style.transform = `translateX(-${index * 100}%)`;
    }

    // Update active states
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('popup-slider__slide--active', i === index);
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('popup-slider__dot--active', i === index);
    });

    this.dispatchEvent(new CustomEvent('slider:changed', {
      bubbles: true,
      detail: { index }
    }));
  }

  get slideCount() {
    return this.slides.length;
  }
}


/**
 * Toggle Switch Component (optional standalone)
 */
class AddonToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');
    
    if (!this.input) {
      this.createInput();
    }
  }

  createInput() {
    this.input = document.createElement('input');
    this.input.type = 'checkbox';
    this.input.className = 'addon-toggle__input';
    this.input.setAttribute('data-addon-checkbox', '');
    
    const slider = document.createElement('span');
    slider.className = 'addon-toggle__slider';
    
    const label = document.createElement('label');
    label.className = 'addon-toggle';
    label.appendChild(this.input);
    label.appendChild(slider);
    
    this.appendChild(label);
  }

  get checked() {
    return this.input?.checked || false;
  }

  set checked(value) {
    if (this.input) {
      this.input.checked = value;
    }
  }

  toggle() {
    if (this.input) {
      this.input.checked = !this.input.checked;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}


// Register Custom Elements
if (!customElements.get('product-addons')) {
  customElements.define('product-addons', ProductAddons);
}

if (!customElements.get('addon-item')) {
  customElements.define('addon-item', AddonItem);
}

if (!customElements.get('addon-popup')) {
  customElements.define('addon-popup', AddonPopup);
}

if (!customElements.get('addon-slider')) {
  customElements.define('addon-slider', AddonSlider);
}

if (!customElements.get('addon-toggle')) {
  customElements.define('addon-toggle', AddonToggle);
}


/**
 * Public API
 */
window.ProductAddons = {
  getContainer: (selector = 'product-addons') => document.querySelector(selector),
  
  getSelectedAddons: (container) => {
    const el = container || document.querySelector('product-addons');
    return el ? el.getSelectedAddons() : [];
  },
  
  getTotalPrice: (container) => {
    const el = container || document.querySelector('product-addons');
    return el ? el.getTotalPrice() : 0;
  },
  
  clearSelection: (container) => {
    const el = container || document.querySelector('product-addons');
    if (el) el.clearSelection();
  }
};